import { DatePipe } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, throwError } from 'rxjs';
import { catchError, exhaustMap, map, switchMap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { AccountInfoState } from '../../account-info-header/models/AccountInfoState';
import { selectInfo, selectAchInstructions } from '../../account-info-header/reducers';
import { PaidForDetails } from '../../shared/models/TrustFieldsRequestInfo';
import { FormatPercentPipe } from '../../shared/pipes/FormatPercentPipe';
import { selectAcknowledgementDateTime } from '../../shared/reducers';
import { StatusViewService } from '../../shared/services/status-view.service';
import { amountTypeDict, transactionDirection } from '../../shared/utilities/Constants';
import { AchActions, TransactionFormActions } from '../actions';
import { AchAcknowledgeApiResponse } from '../models/ach/AchAcknowledgeApiResponse';
import { AchAcknowledgeRequest } from '../models/ach/AchAcknowledgeRequest';
import { AchInfo } from '../models/ach/AchInfo';
import { AchInputApiResponse, AchInputResponse } from '../models/ach/AchInputApiResponse';
import { AchInputRequest } from '../models/ach/AchInputRequest';
import { AchInquiryResponse } from '../models/ach/AchInquiryResponse';
import { AchInquiryRequest } from '../models/ach/AchInquiryRequest';
import { AchVerifyApiResponse } from '../models/ach/AchVerifyApiResponse';
import { AchVerifyRequest } from '../models/ach/AchVerifyRequest';
import { ToFromAccount } from '../models/ach/ToFromAccount';
import { AchExtraPrompts, AchForm, AchTransactionDetails, isAchForm } from '../models/form/TransactionForm';
import { AchStandingAuthDetails, AchStandingAuthForm, isAchFormSA } from '../../standing-authorizations/models/standingAuthorizationForm';
import { PaidFor } from '../models/ptrs/PaidFor';
import { ServiceError, ServiceErrorWithList } from '../models/shared/ServiceError';
import { selectAchInfo, selectAchInfoToFromAccount, selectAchStandingAuthId, selectFiAccountTypeCode, selectIsFromEdit, selectPtrsInfoPaidFor, selectStagedTransactionForm, selectStandingAuthId, selectTransactionForm } from '../reducers';
import { AchService } from '../services/ach.service';
import { StandingAuthAchActions } from '../../standing-authorizations/actions';
import { selectStagedStandingAuthForm, selectStandingAuthAchInfo, selectStandingAuthForm, selectStandingAuthFormPrefilledState, selectStandingAuthIdSA } from '../../standing-authorizations/reducers';
import { currencyStringToNumber } from '../utilities/currencyStringToNumber';
import { selectAchPathEnabled, selectHasAccessInitiateMoneyMovement } from '../../authorize/reducers';
import { DraftListAttributes } from '../../initiate-and-release/Models/Drafts/DraftResponse';
import { selectInitReleaseTransactionInContext } from '../../initiate-and-release/reducers';
import { StandingInstruction } from '../../shared/types/StandingInstruction';
import { concatLatestFrom } from '@ngrx/operators';
import { selectIsAchPathEnabled } from '../../feature-flags/reducers';

@Injectable()
export class AchEffects {
  private actions$ = inject(Actions);
  private achService = inject(AchService);
  private statusViewService = inject(StatusViewService);
  private store = inject(Store);
  private datepipe = inject(DatePipe);

  callAchInput$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AchActions.callAchInput),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectIsFromEdit)
      ]),
      switchMap(([action, accountInfo, isFromEdit]) => {
        if (isFromEdit) {
          return of(TransactionFormActions.editLoading());
        }

        const cashieringRequestId = action.authorizationUniqueId.split('-')[1];
        const correlationId: string = uuidv4();

        const achInputRequest: AchInputRequest = {
          subAccountId: accountInfo.accountId,
          masterAccountId: accountInfo.masterAccount,
          cashieringRequestId: Number(cashieringRequestId),
          correlationId: correlationId
        };

        return this.achService.callAchInput(achInputRequest).pipe(
          map((achInputResponse: AchInputApiResponse) => {
            const achInfo = this.mapInputResponseToAchInfo(achInputResponse);
            return AchActions.inputCallSuccessful({ achInfo });
          }),
          catchError((err: ServiceError) =>
            of(AchActions.inputCallFailed(err)))
        );
      })
    ));

  callAchVerify$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionFormActions.callAchVerify),
      concatLatestFrom(() => [
        this.store.select(selectTransactionForm),
        this.store.select(selectStandingAuthId),
        this.store.select(selectInfo),
        this.store.select(selectAchInfo),
        this.store.select(selectHasAccessInitiateMoneyMovement),
        this.store.select(selectPtrsInfoPaidFor),
        this.store.select(selectInitReleaseTransactionInContext),
        this.store.select(selectAchInstructions),
        this.store.select(selectAchPathEnabled),
        this.store.select(selectIsAchPathEnabled)
      ]),
      exhaustMap(([, transactionFormState, standingAuthId, accountInfo, achInfo, hasMmInitiateAccess, ptrsInfo, initReleaseTransactionInContext, achInstructions, hasPathFSG, pathFlaggedOn]) => {
        if (transactionFormState && isAchForm(transactionFormState)
          && !(accountInfo.classifications.isIip
            && transactionFormState.extraPrompts.transferDirection === transactionDirection.out.toLowerCase())) {
          const correlationId: string = uuidv4();
          const isPathEnabled = hasPathFSG || pathFlaggedOn;
          const request = hasMmInitiateAccess
            ? this.createAchVerifyRequestForInR(
              transactionFormState,
              transactionFormState.transactionDetails,
              standingAuthId,
              accountInfo,
              achInfo,
              correlationId,
              ptrsInfo,
              initReleaseTransactionInContext,
              achInstructions,
              isPathEnabled
            )
            : this.createAchVerifyRequest(
              transactionFormState,
              transactionFormState.transactionDetails,
              standingAuthId,
              accountInfo,
              achInfo,
              correlationId,
              isPathEnabled
            );

          return this.achService.callAchVerify(request, isPathEnabled).pipe(
            map((response: AchVerifyApiResponse) => {
              return AchActions.verifyCallSuccessful({
                response,
                initiationDate: achInfo.cashieringDates.initiationDate
              });
            }),
            catchError((err) =>
              of(AchActions.verifyCallFailed(err)))
          );
        }
        return throwError(() => new Error('Invalid form for ach verify call'));
      })
    ));

  callAchAddProfileVerify$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionFormActions.callAchAddProfileVerify),
      concatLatestFrom(() => [
        this.store.select(selectTransactionForm),
        this.store.select(selectStandingAuthId),
        this.store.select(selectInfo),
        this.store.select(selectAchInfo),
        this.store.select(selectPtrsInfoPaidFor),
        this.store.select(selectInitReleaseTransactionInContext),
        this.store.select(selectAchInstructions),
        this.store.select(selectAchPathEnabled),
        this.store.select(selectIsAchPathEnabled)
      ]),
      exhaustMap(([action, transactionFormState, standingAuthId, accountInfo, achInfo, ptrsInfo, initReleaseTransactionInContext, achInstructions, hasPathFSG, pathFlaggedOn]) => {
        if (transactionFormState && isAchForm(transactionFormState)
          && !(accountInfo.classifications.isIip
            && transactionFormState.extraPrompts.transferDirection === transactionDirection.out.toLowerCase())) {
          const correlationId: string = uuidv4();
          const isPathEnabled = hasPathFSG || pathFlaggedOn;

          const request = action.isInRViewReviewFlow
            ? this.createAchAddProfileVerifyRequestForInR(
              transactionFormState,
              transactionFormState.transactionDetails,
              standingAuthId,
              accountInfo,
              achInfo,
              correlationId,
              ptrsInfo,
              initReleaseTransactionInContext,
              achInstructions,
              isPathEnabled
            )
            : this.createAchAddProfileVerifyRequest(
              transactionFormState,
              transactionFormState.transactionDetails,
              standingAuthId,
              accountInfo,
              achInfo,
              correlationId,
              achInstructions,
              isPathEnabled
            );
          return this.achService.callAchVerify(request, isPathEnabled).pipe(
            map((response: AchVerifyApiResponse) => {
              return AchActions.verifyCallSuccessful({
                response,
                initiationDate: achInfo.cashieringDates.initiationDate
              });
            }),
            catchError((err) =>
              of(AchActions.verifyCallFailed(err)))
          );
        }
        return throwError(() => new Error('Invalid form for ach verify call'));
      })
    ));

  callAchVerifyforCreateStandingAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StandingAuthAchActions.callAchVerify),
      concatLatestFrom(() => [
        this.store.select(selectStandingAuthForm),
        this.store.select(selectStandingAuthIdSA),
        this.store.select(selectInfo),
        this.store.select(selectStandingAuthAchInfo),
        this.store.select(selectStandingAuthFormPrefilledState)
      ]),
      exhaustMap(([, StandingAuthorizationFormState, standingAuthId, accountInfo, achInfo, prefilledFlowState]) => {
        if (StandingAuthorizationFormState && isAchFormSA(StandingAuthorizationFormState)
          && !(accountInfo.classifications.isIip
            && StandingAuthorizationFormState.extraPrompts.transferDirection === transactionDirection.out.toLowerCase())) {
          const correlationId: string = uuidv4();

          const request = this.createAchVerifyRequestStandingAuth(
            StandingAuthorizationFormState,
            StandingAuthorizationFormState.standingAuthorizationDetails,
            standingAuthId,
            accountInfo,
            achInfo,
            correlationId
          );
          return this.achService.callAchVerify(request).pipe(
            map((response: AchVerifyApiResponse) => {
              if (!response.messageFields.hasError && !response.messageFields.hasWarning) {
                return StandingAuthAchActions.verifyCallSuccessful({
                  response,
                  initiationDate: achInfo.cashieringDates.initiationDate
                });
              } else if (!response.messageFields.hasError && response.messageFields.hasWarning) {
                for (let i = 0; i < response.messageFields.warningList.length; i++) {
                  //If an advisor's current account level federal election is 10% or higher and they're electing something 9% (e.g. 15% -> 5% or 30% -> 9%) or under
                  //OR if an advisors current account level federal election is 9% or lower and they're electing a lower percentage (e.g. 9% -> 5% or 2% -> 1%)
                  //We DO NOT ALLOW them to proceed when the CTWE Warning Message appears
                  if (response.messageFields.warningList[i].messageField === 'CTWE' && (prefilledFlowState.state === 'started')) {
                    return StandingAuthAchActions.verifyCallFinishedWithError({ response });
                  }
                }
                return StandingAuthAchActions.verifyCallSuccessful({
                  response,
                  initiationDate: achInfo.cashieringDates.initiationDate
                });
              } else {
                return StandingAuthAchActions.verifyCallFinishedWithError({ response });
              }
            }),
            catchError((err) =>
              of(StandingAuthAchActions.verifyCallFailed(err)))
          );
        }
        return throwError(() => new Error('Invalid form for ach verify call'));
      })
    ));

  callAchAcknowledge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AchActions.callAcknowledge),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectAchStandingAuthId),
        this.store.select(selectStagedTransactionForm),
        this.store.select(selectAcknowledgementDateTime),
        this.store.select(selectPtrsInfoPaidFor),
        this.store.select(selectAchInfoToFromAccount),
        this.store.select(selectFiAccountTypeCode),
        this.store.select(selectAchInfo),
        this.store.select(selectInitReleaseTransactionInContext)
      ]),
      switchMap(([, accountInfo, standingAuthId, transactionFormState, acknowledgementConstentDateTime, paidForInfo, toFromAccount, fiAccountTypeCode, achInfo, initReleaseTransactionInContext]) => {
        if (!(accountInfo.classifications.isIip
          && (transactionFormState.extraPrompts as AchExtraPrompts).transferDirection === transactionDirection.out.toLowerCase())) {
          const request = this.createAchAcknowledgeRequest(
            accountInfo,
            standingAuthId,
            transactionFormState as AchForm,
            acknowledgementConstentDateTime,
            paidForInfo,
            toFromAccount,
            fiAccountTypeCode,
            achInfo,
            initReleaseTransactionInContext
          );
          return this.achService.callAchAcknowledge(request).pipe(
            map((response: AchAcknowledgeApiResponse) => {
              return AchActions.acknowledgeCallSuccessful({ response });
            }),
            catchError((err: ServiceErrorWithList) =>
              of(AchActions.acknowledgeCallFail(err)))
          );
        }
        return throwError(() => new Error('Invalid form for ach acknowledge call'));
      })
    ));

  callAchAddProfileAcknowledge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AchActions.callAddProfileAcknowledge),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectStandingAuthId),
        this.store.select(selectStagedTransactionForm),
        this.store.select(selectAcknowledgementDateTime),
        this.store.select(selectPtrsInfoPaidFor),
        this.store.select(selectAchInfoToFromAccount),
        this.store.select(selectFiAccountTypeCode),
        this.store.select(selectAchInfo),
        this.store.select(selectInitReleaseTransactionInContext)
      ]),
      switchMap(([, accountInfo, standingAuthId, transactionFormState, acknowledgementConstentDateTime, paidForInfo, toFromAccount, fiAccountTypeCode, achInfo, initReleaseTransactionInContext]) => {
        if (!(accountInfo.classifications.isIip
          && (transactionFormState.extraPrompts as AchExtraPrompts).transferDirection === transactionDirection.out.toLowerCase())) {
          const request = this.createAchAddProfileAcknowledgeRequest(
            accountInfo,
            standingAuthId,
            transactionFormState as AchForm,
            acknowledgementConstentDateTime,
            paidForInfo,
            toFromAccount,
            fiAccountTypeCode,
            achInfo,
            initReleaseTransactionInContext
          );
          return this.achService.callAchAcknowledge(request).pipe(
            map((response: AchAcknowledgeApiResponse) => {
              return AchActions.acknowledgeCallSuccessful({ response });
            }),
            catchError((err: ServiceErrorWithList) =>
              of(AchActions.acknowledgeCallFail(err)))
          );
        }
        return throwError(() => new Error('Invalid form for ach acknowledge call'));
      })
    ));

  callAchAcknowledgeforStandingAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StandingAuthAchActions.callAcknowledge),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectStandingAuthIdSA),
        this.store.select(selectStagedStandingAuthForm),
        this.store.select(selectAcknowledgementDateTime),
        this.store.select(selectStandingAuthAchInfo),
      ]),
      switchMap(([, accountInfo, standingAuthId, standingAuthFormState, acknowledgementConstentDateTime, achInfo]) => {
        if (!(accountInfo.classifications.isIip
          && (standingAuthFormState.extraPrompts as AchExtraPrompts).transferDirection === transactionDirection.out.toLowerCase())) {
          const request = this.createAchAcknowledgeRequestStandingAuth(
            accountInfo,
            standingAuthId,
            standingAuthFormState as AchStandingAuthForm,
            acknowledgementConstentDateTime,
            achInfo
          );
          return this.achService.callAchAcknowledge(request).pipe(
            map((response: AchAcknowledgeApiResponse) => {
              if (!response.messageFields.hasError) {
                return StandingAuthAchActions.acknowledgeCallSuccessful({ response });
              } else {
                return StandingAuthAchActions.acknowledgeCallFinishedWithError({ response });
              }
            }),
            catchError((err: ServiceErrorWithList) =>
              of(StandingAuthAchActions.acknowledgeCallFail(err)))
          );
        }
        return throwError(() => new Error('Invalid form for ach acknowledge call'));
      })
    ));

  callInquiry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AchActions.callInquiry),
      concatLatestFrom(() => [this.store.select(selectInfo)]),
      switchMap(([action, accountInfoState]) => {
        const payload: AchInquiryRequest = {
          cashieringRequestId: Number(action.payload.cashieringRqstId),
          cashieringRqstSecondId: Number(action.payload.cashieringRqstSecondId),
          isFromView: true,
          subAccountId: accountInfoState.accountId,
          masterAccountId: accountInfoState.masterAccount,
          isReactivate: false,
          recTypeCode: action.payload.txnCode ?? '',
          thirdPartyIndicator: ''
        };

        return this.statusViewService.callAchInquiry(payload).pipe(
          map((achInquiryResponse: AchInquiryResponse) => {
            return AchActions.inquiryCallSuccessful({ achInquiryResponse });
          }),
          catchError((err: ServiceError) => of(AchActions.inquiryCallFailed(err)))
        );
      })
    ));

  callInquiryforCreateStandingAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StandingAuthAchActions.callAchInquiry),
      concatLatestFrom(() => [this.store.select(selectInfo)]),
      switchMap(([action, accountInfoState]) => {
        const payload: AchInquiryRequest = {
          cashieringRequestId: Number(action.payload.cashieringRqstId),
          cashieringRqstSecondId: Number(action.payload.cashieringRqstSecondId),
          isFromView: true,
          subAccountId: accountInfoState.accountId,
          masterAccountId: accountInfoState.masterAccount,
          isReactivate: false,
          recTypeCode: action.payload.txnCode ?? '',
          thirdPartyIndicator: '',
        };

        return this.statusViewService.callAchInquiry(payload).pipe(
          map((achInquiryResponse: AchInquiryResponse) => {
            return StandingAuthAchActions.inquiryCallSuccessful({ achInquiryResponse, });
          }),
          catchError((err: ServiceError) =>
            of(StandingAuthAchActions.inquiryCallFailed(err)))
        );
      })
    ));

  mapInputResponseToAchInfo(achInputResponse: AchInputApiResponse): AchInputResponse {
    const percentFormatPipe = new FormatPercentPipe();
    return {
      distributionReason: achInputResponse.distributionReasonDescription,
      distributionReasonsList: achInputResponse.distributionReasonsList,
      stateCd: achInputResponse.stateCd,
      stateTypeIndicator: achInputResponse.stateTypeIndicator,
      withholdFedTax: achInputResponse.iraFields.isFederalTaxWithheld,
      withholdStateTax: achInputResponse.iraFields.isStateTaxWithheld,
      fedTaxPercent: percentFormatPipe.transform(achInputResponse.iraFields.fedTaxFormattedPercentage),
      stateTaxPercent: achInputResponse.iraFields.stateTaxFormattedPercentage,
      stateTaxAttentionRequiredMI: achInputResponse.iraFields.stateTaxAttentionRequiredMI,
      stateTaxAttentionRequiredOther: achInputResponse.iraFields.stateTaxAttentionRequiredOther,
      contributionOptions: achInputResponse.contributionYears.map((contributionYear) => contributionYear + ' - ' + achInputResponse.contributionDescriptionText),
      contributionCode: achInputResponse.contributionCode,
      contributionSubCode: achInputResponse.contributionSubCode,
      maxTransferAmount: achInputResponse.maxTransferAmount,
      toFromAccountNumber: achInputResponse.toFromAccount.accountNumber,
      amountType: achInputResponse.iraFields.grossNetIndicator === 'G' ? amountTypeDict.gross : achInputResponse.iraFields.grossNetIndicator === 'N' ? amountTypeDict.net : '',
      isIraTransaction: achInputResponse.isIraTransaction,
      iraFields: achInputResponse.iraFields,
      trustTaxCategoriesAndCodes: achInputResponse.trustFieldsResponse.taxCodeList,
      trustIncomePercent: achInputResponse.trustFieldsResponse.trustIncmPct,
      trustPrincipalPercent: achInputResponse.trustFieldsResponse.trustPrinPct,
      availableCashMargin: achInputResponse.cashInfo.cusAvailableOnMargin,
      availableCashMMF: achInputResponse.cashInfo.cusAvailableCashMMF,
      toFromAccount: achInputResponse.toFromAccount,
      fiAccountTypeCode: achInputResponse.fiAccountTypeCode,
      frequencyList: achInputResponse.frequencyList,
      cashieringIraAgreementText: achInputResponse.cashieringIraAgreementText,
      processDate: achInputResponse.processDate,
      cashieringRequestId: achInputResponse.cashieringRequestId,
    };
  }

  createAchVerifyRequest(achTransactionForm: AchForm, transactionDetails: AchTransactionDetails, authorizationId: string, accountInfo: AccountInfoState, achInfo: AchInfo, correlationId: string, isPathEnabled: boolean): AchVerifyRequest {
    const cashieringRequestId = +authorizationId.split('-')[1];
    const request: AchVerifyRequest = {
      subAccountId: accountInfo.accountId,
      masterAccountId: accountInfo.masterAccount,
      processDate:
                (achTransactionForm.extraPrompts.transferDirection === 'incoming'
                  ? this.datepipe.transform(transactionDetails.settlementDate, 'MM/dd/yyyy')
                  : this.datepipe.transform(transactionDetails.completionDate, 'MM/dd/yyyy')) ?? '',
      endDate: '',
      secondProcessDate: '',
      frequency: '',
      cashieringRequestId: cashieringRequestId,
      contributionYear: transactionDetails.contributionReason?.substring(0, 4),
      stateTaxRate: transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', ''),
      fedTaxRate: transactionDetails.taxWithholdingsFormGroup?.fedTaxPercent.replace('%', ''),
      isEdit: false,
      grossNetIndicator: transactionDetails.amountType === amountTypeDict.gross, //true for Gross, false for Net
      stateTaxAttentionRequiredOther: achInfo.stateTaxAttentionRequiredOther,
      stateTaxAttentionRequiredMI: achInfo.stateTaxAttentionRequiredMI,
      amount: currencyStringToNumber(transactionDetails.amount).toString(),
      correlationId: correlationId,
      irsDistributionCode: achInfo.distributionReasonsList?.find((element) => element.description === transactionDetails.taxWithholdingsFormGroup?.distributionReason)?.code.toString() ?? ' ',
      irsDistributionSubCode: achInfo.distributionReasonsList?.find((element) => element.description === transactionDetails.taxWithholdingsFormGroup?.distributionReason)?.subCode.toString() ?? ' ',
      isWithholdFedTax: (transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', '') === ''
        ? '0'
        : transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', '')) !== '0'
        ? true
        : false,
      isWithholdStateTax: (transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', '') === ''
        ? '0'
        : transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', '')) !== '0'
        ? true
        : false,
      abaNumber: achInfo.toFromAccount?.abaNumber ?? '',
      financialInstitution: '',
      transferAccount: '',
      accountName: '',
      accountType: '',
      fundsDirection: '',
      distributionReasonDescription: '',
      initiatedBy: '',
      trustTaxCode: '',
      paidFor: new PaidForDetails(),
      customerId: achTransactionForm.extraPrompts?.requestingAccountHolderId ? achTransactionForm.extraPrompts?.requestingAccountHolderId : '',
      addProfile: false,
      transferId: this.getTransferIdForContext(achInfo, cashieringRequestId)
    };
    if (isPathEnabled) {
      request.fundsDirection = achTransactionForm.extraPrompts.transferDirection === 'incoming' ? 'I' : 'O';
      request.isIraTransaction = accountInfo?.classifications?.isIra ?? false;
      request.tranMemo = transactionDetails.trustComment ?? '';
      request.addProfileAttributes = {
        fundsDirection: achTransactionForm.extraPrompts.transferDirection === 'incoming' ? 'DEP' : 'DIS',
        bankAccountTypeCode: '',
        bankAccountId: '',
        aba: '',
        maxTransferAmount: 0,
        bankAccountTitleName: '',
        contributionCode: achInfo.contributionCode ?? '',
        contributionSubCode: achInfo.contributionSubCode ?? '',
        financialInstitution: ''
      };
    }
    return request;
  }

  createAchVerifyRequestForInR(
    achTransactionForm: AchForm,
    transactionDetails: AchTransactionDetails,
    authorizationId: string,
    accountInfo: AccountInfoState,
    achInfo: AchInfo,
    correlationId: string,
    ptrsInfo: PaidFor,
    initReleaseTransactionInContext: DraftListAttributes,
    achInstructions: StandingInstruction[],
    isPathEnabled: boolean
  ): AchVerifyRequest {
    const cashieringReqId = authorizationId.includes('-') ? +authorizationId.split('-')[1] : parseInt(authorizationId);
    const thirdPartyInd = achInstructions?.find((x) => x?.cashieringRqstId === cashieringReqId?.toString())?.isThirdParty ?? false;
    const request: AchVerifyRequest = {
      draftId: initReleaseTransactionInContext.draftId,
      subAccountId: accountInfo.accountId,
      masterAccountId: accountInfo.masterAccount,
      processDate:
                (achTransactionForm.extraPrompts.transferDirection === 'incoming'
                  ? this.datepipe.transform(transactionDetails.settlementDate, 'MM/dd/yyyy')
                  : this.datepipe.transform(transactionDetails.completionDate, 'MM/dd/yyyy')) ?? '',
      endDate: '',
      secondProcessDate: '',
      frequency: Boolean(initReleaseTransactionInContext?.draftObject?.frequency) ? (initReleaseTransactionInContext?.draftObject?.frequency?.toString() ?? '') : (achTransactionForm.extraPrompts?.frequency ?? ''),
      cashieringRequestId: cashieringReqId,
      contributionYear: Boolean(initReleaseTransactionInContext?.draftObject?.contributionYear) ? (initReleaseTransactionInContext?.draftObject?.contributionYear ?? '') : transactionDetails.contributionReason?.substring(0, 4),
      contributionType: transactionDetails.contributionReason?.replace(' - ', ' '), //used by Legacy UI to determine if contribution type is still valid
      stateTaxRate: transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', ''),
      fedTaxRate: transactionDetails.taxWithholdingsFormGroup?.fedTaxPercent.replace('%', ''),
      isEdit: false,
      grossNetIndicator: transactionDetails.amountType === amountTypeDict.gross, //true for Gross, false for Net
      stateTaxAttentionRequiredOther: achInfo.stateTaxAttentionRequiredOther,
      stateTaxAttentionRequiredMI: achInfo.stateTaxAttentionRequiredMI,
      amount: currencyStringToNumber(transactionDetails.amount).toString(),
      correlationId: correlationId,
      irsDistributionCode: achInfo.distributionReasonsList?.find((element) => element.description === transactionDetails.taxWithholdingsFormGroup?.distributionReason)?.code.toString() ?? ' ',
      irsDistributionSubCode: achInfo.distributionReasonsList?.find((element) => element.description === transactionDetails.taxWithholdingsFormGroup?.distributionReason)?.subCode.toString() ?? ' ',
      isWithholdFedTax: (transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', '') === ''
        ? '0'
        : transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', '')) !== '0'
        ? true
        : false,
      isWithholdStateTax: (transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', '') === ''
        ? '0'
        : transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', '')) !== '0'
        ? true
        : false,
      abaNumber: achInfo.toFromAccount?.abaNumber ?? '',
      financialInstitution: achInfo?.toFromAccount?.financialInstitution ?? '',
      accountType: achInfo?.toFromAccount?.accountType ?? '',
      accountName: achInfo?.toFromAccount?.accountName ?? '',
      transferAccount: achInfo?.toFromAccount?.accountNumber ?? '',
      fundsDirection: achTransactionForm?.extraPrompts?.transferDirection === 'incoming' ? 'I' : 'O',
      distributionReasonDescription: achTransactionForm?.transactionDetails?.taxWithholdingsFormGroup?.distributionReason ?? '',
      initiatedBy: accountInfo?.userName,
      trustTaxCategory: transactionDetails?.trustTaxCategory ?? '',
      trustTaxCode: transactionDetails?.trustTaxCode ?? '',
      isTrust: accountInfo.classifications.isPtrs,
      paidFor: this.getPaidForDetails(ptrsInfo, achTransactionForm?.transactionDetails),
      trustIncmPct: transactionDetails?.trustIncomePercent ? transactionDetails?.trustIncomePercent.replace('%', '') : '',
      trustPrinPct: transactionDetails?.trustPrincipalPercent ? transactionDetails?.trustPrincipalPercent.replace('%', '') : '',
      fundsAvailableToTrade: transactionDetails?.fundsAvailableToTrade ?? '',
      requestedWithdrawalDate: transactionDetails?.withdrawalDate ?? '',
      thirdPartyIndValue: thirdPartyInd ? 'Y' : 'N',
      isIraTransaction: accountInfo?.classifications?.isIra ?? false,
      customerId: achTransactionForm.extraPrompts?.requestingAccountHolderId ? achTransactionForm.extraPrompts?.requestingAccountHolderId : '',
      addProfile: false,
      transferId: this.getTransferIdForContext(achInfo, cashieringReqId)
    };
    if (isPathEnabled) {
      request.tranMemo = transactionDetails.trustComment ?? '';
      request.addProfileAttributes = {
        fundsDirection: achTransactionForm.extraPrompts.transferDirection === 'incoming' ? 'DEP' : 'DIS',
        bankAccountTypeCode: '',
        bankAccountId: '',
        aba: '',
        maxTransferAmount: 0,
        bankAccountTitleName: '',
        contributionCode: achInfo.contributionCode ?? '',
        contributionSubCode: achInfo.contributionSubCode ?? '',
        financialInstitution: ''
      };
    }
    return request;
  }

  createAchAddProfileVerifyRequestForInR(
    achTransactionForm: AchForm,
    transactionDetails: AchTransactionDetails,
    authorizationId: string,
    accountInfo: AccountInfoState,
    achInfo: AchInfo,
    correlationId: string,
    ptrsInfo: PaidFor,
    initReleaseTransactionInContext: DraftListAttributes,
    achInstructions: StandingInstruction[],
    isPathEnabled: boolean
  ): AchVerifyRequest {
    const cashieringReqId = authorizationId.includes('-') ? +authorizationId.split('-')[1] : parseInt(authorizationId);
    const thirdPartyInd = achInstructions?.find((x) => x?.cashieringRqstId === cashieringReqId?.toString())?.isThirdParty ?? false;
    const request: AchVerifyRequest = {
      draftId: initReleaseTransactionInContext.draftId,
      subAccountId: accountInfo.accountId,
      masterAccountId: accountInfo.masterAccount,
      processDate:
                (achTransactionForm.extraPrompts.transferDirection === 'incoming'
                  ? this.datepipe.transform(transactionDetails.settlementDate, 'MM/dd/yyyy')
                  : this.datepipe.transform(transactionDetails.completionDate, 'MM/dd/yyyy')) ?? '',
      endDate: this.datepipe.transform(transactionDetails.endDate, 'MM/dd/yyyy') ?? '',
      secondProcessDate: this.datepipe.transform(transactionDetails.secondProcessDate, 'MM/dd/yyyy') ?? '',
      frequency: Boolean(initReleaseTransactionInContext?.draftObject?.frequency) ? (initReleaseTransactionInContext?.draftObject?.frequency?.toString() ?? '') : (achTransactionForm.extraPrompts?.frequency ?? ''),
      cashieringRequestId: cashieringReqId,
      contributionYear: Boolean(initReleaseTransactionInContext?.draftObject?.contributionYear) ? (initReleaseTransactionInContext?.draftObject?.contributionYear ?? '') : transactionDetails.contributionReason?.substring(0, 4),
      contributionType: transactionDetails.contributionReason?.replace(' - ', ' '), //used by Legacy UI to determine if contribution type is still valid
      stateTaxRate: transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', ''),
      fedTaxRate: transactionDetails.taxWithholdingsFormGroup?.fedTaxPercent.replace('%', ''),
      isEdit: false,
      grossNetIndicator: transactionDetails.amountType === amountTypeDict.gross, //true for Gross, false for Net
      stateTaxAttentionRequiredOther: achInfo.stateTaxAttentionRequiredOther,
      stateTaxAttentionRequiredMI: achInfo.stateTaxAttentionRequiredMI,
      amount: currencyStringToNumber(transactionDetails.amount).toString(),
      correlationId: correlationId,
      irsDistributionCode: achInfo.distributionReasonsList?.find((element) => element.description === transactionDetails.taxWithholdingsFormGroup?.distributionReason)?.code.toString() ?? ' ',
      irsDistributionSubCode: achInfo.distributionReasonsList?.find((element) => element.description === transactionDetails.taxWithholdingsFormGroup?.distributionReason)?.subCode.toString() ?? ' ',
      isWithholdFedTax: (transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', '') === ''
        ? '0'
        : transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', '')) !== '0'
        ? true
        : false,
      isWithholdStateTax: (transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', '') === ''
        ? '0'
        : transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', '')) !== '0'
        ? true
        : false,
      addProfile: true,
      addProfileAttributes: {
        fundsDirection: initReleaseTransactionInContext?.draftObject?.addProfileAttributes?.fundsDirection === 'DEP' ? 'DEP' : 'DIS',
        bankAccountTypeCode: initReleaseTransactionInContext?.draftObject?.addProfileAttributes?.bankAccountTypeCode ?? '',
        bankAccountId: initReleaseTransactionInContext?.draftObject?.addProfileAttributes?.bankAccountId ?? '',
        aba: achInfo.toFromAccount.abaNumber ?? '',
        maxTransferAmount: initReleaseTransactionInContext?.draftObject?.addProfileAttributes?.maxTransferAmount ?? 0,
        bankAccountTitleName: initReleaseTransactionInContext?.draftObject?.addProfileAttributes?.bankAccountTitleName ?? '',
        contributionCode: initReleaseTransactionInContext?.draftObject?.addProfileAttributes?.contributionCode ?? '',
        contributionSubCode: initReleaseTransactionInContext?.draftObject?.addProfileAttributes?.contributionSubCode ?? '',
        financialInstitution: initReleaseTransactionInContext?.draftObject?.addProfileAttributes?.financialInstitution ?? ''
      },
      abaNumber: achInfo.toFromAccount?.abaNumber ?? '',
      financialInstitution: '',
      accountType: '',
      accountName: '',
      transferAccount: '',
      fundsDirection: isPathEnabled ? (achTransactionForm.extraPrompts.transferDirection === 'incoming' ? 'I' : 'O') : '',
      distributionReasonDescription: achTransactionForm?.transactionDetails?.taxWithholdingsFormGroup?.distributionReason ?? '',
      initiatedBy: accountInfo?.userName,
      trustTaxCategory: transactionDetails?.trustTaxCategory ?? '',
      trustTaxCode: transactionDetails?.trustTaxCode ?? '',
      isTrust: accountInfo.classifications.isPtrs,
      paidFor: this.getPaidForDetails(ptrsInfo, achTransactionForm?.transactionDetails),
      trustIncmPct: transactionDetails?.trustIncomePercent ? transactionDetails?.trustIncomePercent.replace('%', '') : '',
      trustPrinPct: transactionDetails?.trustPrincipalPercent ? transactionDetails?.trustPrincipalPercent.replace('%', '') : '',
      fundsAvailableToTrade: transactionDetails?.fundsAvailableToTrade ?? '',
      requestedWithdrawalDate: transactionDetails?.withdrawalDate ?? '',
      thirdPartyIndValue: thirdPartyInd ? 'Y' : 'N',
      isIraTransaction: accountInfo?.classifications?.isIra ?? false,
      customerId: achTransactionForm.extraPrompts?.requestingAccountHolderId ? achTransactionForm.extraPrompts?.requestingAccountHolderId : '',
      transferId: this.getTransferIdForContext(achInfo, cashieringReqId)
    };
    if (isPathEnabled) {
      request.tranMemo = transactionDetails.trustComment ?? '';
    }
    return request;
  }

  createAchAddProfileVerifyRequest(achTransactionForm: AchForm, transactionDetails: AchTransactionDetails, authorizationId: string, accountInfo: AccountInfoState, achInfo: AchInfo, correlationId: string, achInstructions: StandingInstruction[], isPathEnabled: boolean): AchVerifyRequest {
    const fedTaxRate = transactionDetails.taxWithholdingsFormGroup?.fedTaxPercent.replace('%', '') ?? '';
    const stateTaxRate = transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', '') ?? '';
    const cashieringReqId = authorizationId.includes('-') ? +authorizationId.split('-')[1] : parseInt(authorizationId);
    const thirdPartyInd = achInstructions?.find((x) => x?.cashieringRqstId === cashieringReqId?.toString())?.isThirdParty ?? false;
    const verifyRequest: AchVerifyRequest = {
      subAccountId: accountInfo.accountId,
      masterAccountId: accountInfo.masterAccount,
      processDate: this.datepipe.transform(transactionDetails.completionDate, 'MM/dd/yyyy') ?? '',
      endDate: this.datepipe.transform(transactionDetails.endDate, 'MM/dd/yyyy') ?? '',
      secondProcessDate: this.datepipe.transform(transactionDetails.secondProcessDate, 'MM/dd/yyyy') ?? '',
      frequency: achTransactionForm.extraPrompts?.frequency === 'onetime' ? 'On-Request' : achInfo.frequencyList.find((x) => x.code === transactionDetails.recurringFrequency)?.description ?? '',
      cashieringRequestId: cashieringReqId,
      contributionYear: transactionDetails.contributionReason?.substring(0, 4),
      stateTaxRate: stateTaxRate,
      fedTaxRate: fedTaxRate,
      isEdit: false,
      grossNetIndicator: transactionDetails.amountType === amountTypeDict.gross, //true for Gross, false for Net
      stateTaxAttentionRequiredOther: achInfo.stateTaxAttentionRequiredOther,
      stateTaxAttentionRequiredMI: achInfo.stateTaxAttentionRequiredMI,
      amount: currencyStringToNumber(transactionDetails.amount).toString(),
      correlationId: correlationId,
      irsDistributionCode: achInfo.distributionReasonsList?.find((element) => element.description === transactionDetails.taxWithholdingsFormGroup?.distributionReason)?.code.toString() ?? ' ',
      irsDistributionSubCode: achInfo.distributionReasonsList?.find((element) => element.description === transactionDetails.taxWithholdingsFormGroup?.distributionReason)?.subCode.toString() ?? ' ',
      isWithholdFedTax: fedTaxRate !== '0' && fedTaxRate !== '',
      isWithholdStateTax: stateTaxRate !== '0' && stateTaxRate !== '',
      addProfile: true,
      addProfileAttributes: {
        fundsDirection: achTransactionForm.extraPrompts.transferDirection === 'incoming' ? 'DEP' : 'DIS',
        bankAccountTypeCode: achInfo.fiAccountType,
        bankAccountId: achInfo.toFromAccount.accountNumber,
        aba: achInfo.toFromAccount.abaNumber,
        maxTransferAmount: Number(achInfo.maxTransferAmount?.replace('$', '').split(',')
          .join('')),
        bankAccountTitleName: achInfo.toFromAccount.accountName,
        contributionCode: achInfo.contributionCode,
        contributionSubCode: achInfo.contributionSubCode,
        financialInstitution: achInfo.toFromAccount.financialInstitution
      },
      abaNumber: achInfo.toFromAccount?.abaNumber ?? '',
      financialInstitution: '',
      transferAccount: '',
      accountName: '',
      accountType: '',
      fundsDirection: achTransactionForm?.extraPrompts?.transferDirection === 'incoming' ? 'I' : 'O',
      distributionReasonDescription: achTransactionForm?.transactionDetails?.taxWithholdingsFormGroup?.distributionReason ?? '',
      initiatedBy: '',
      trustTaxCode: '',
      paidFor: new PaidForDetails(),
      trustIncmPct: '',
      trustPrinPct: '',
      fundsAvailableToTrade: transactionDetails?.fundsAvailableToTrade ?? '',
      requestedWithdrawalDate: transactionDetails?.withdrawalDate ?? '',
      thirdPartyIndValue: thirdPartyInd ? 'Y' : 'N',
      isIraTransaction: accountInfo?.classifications?.isIra ?? false,
      isTrust: accountInfo.classifications.isPtrs,
      customerId: achTransactionForm.extraPrompts?.requestingAccountHolderId ? achTransactionForm.extraPrompts?.requestingAccountHolderId : '',
      transferId: this.getTransferIdForContext(achInfo, cashieringReqId)
    };
    if (isPathEnabled) {
      verifyRequest.tranMemo = transactionDetails.trustComment ?? '';
    }
    return verifyRequest;
  }

  createAchVerifyRequestStandingAuth(achStandingAuthForm: AchStandingAuthForm, standingAuthDetails: AchStandingAuthDetails, authorizationId: string, accountInfo: AccountInfoState, achInfo: AchInfo, correlationId: string): AchVerifyRequest {
    return {
      subAccountId: accountInfo.accountId,
      masterAccountId: accountInfo.masterAccount,
      processDate: this.GetProcessDateVerify(standingAuthDetails, achStandingAuthForm),
      endDate: this.datepipe.transform(standingAuthDetails.endDate, 'MM/dd/yyyy') ?? '12/31/9999',
      secondProcessDate: this.datepipe.transform(standingAuthDetails.secondProcessDate, 'MM/dd/yyyy') ?? '12/31/9999',
      frequency: achInfo?.frequencyList ? achInfo.frequencyList.find((frequency) => frequency.code === standingAuthDetails.recurringFrequency)?.description ?? standingAuthDetails.recurringFrequency : standingAuthDetails.recurringFrequency,
      cashieringRequestId: +authorizationId.split('-')[1],
      contributionYear: standingAuthDetails.contributionReason?.substring(0, 4),
      stateTaxRate: standingAuthDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', '') === ''
        ? '0'
        : standingAuthDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', ''),
      fedTaxRate: standingAuthDetails.taxWithholdingsFormGroup?.fedTaxPercent.replace('%', '') === ''
        ? '0'
        : standingAuthDetails.taxWithholdingsFormGroup?.fedTaxPercent.replace('%', ''),
      isEdit: true,
      grossNetIndicator: standingAuthDetails.amountType === amountTypeDict.gross,
      stateTaxAttentionRequiredOther: achInfo.stateTaxAttentionRequiredOther,
      stateTaxAttentionRequiredMI: achInfo.stateTaxAttentionRequiredMI,
      amount: currencyStringToNumber(standingAuthDetails.amount).toString(),
      correlationId: correlationId,
      irsDistributionCode: achInfo.distributionReasonMap?.find((element) => element.description === standingAuthDetails.taxWithholdingsFormGroup?.distributionReason)?.code.toString() ?? ' ',
      irsDistributionSubCode: achInfo.distributionReasonMap?.find((element) => element.description === standingAuthDetails.taxWithholdingsFormGroup?.distributionReason)?.subCode.toString() ?? ' ',
      isWithholdFedTax: (standingAuthDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', '') === ''
        ? '0'
        : standingAuthDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', '')) !== '0'
        ? true
        : false,
      isWithholdStateTax: (standingAuthDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', '') === ''
        ? '0'
        : standingAuthDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', '')) !== '0'
        ? true
        : false,
      abaNumber: achInfo.toFromAccount?.abaNumber ?? '',
      fundsDirection: '',
      distributionReasonDescription: '',
      initiatedBy: '',
      financialInstitution: '',
      accountName: '',
      transferAccount: '',
      accountType: '',
      trustTaxCode: '',
      paidFor: new PaidForDetails(),
      addProfile: false
    };
  }

  createAchAddProfileAcknowledgeRequest(
    accountInfo: AccountInfoState,
    authorizationId: string,
    transactionForm: AchForm,
    acknowledgementConsentDateTime: string,
    paidForInfo: PaidFor,
    toFromAccount: ToFromAccount,
    fiAccountTypeCode: string,
    achInfo: AchInfo,
    initReleaseTransactionInContext: DraftListAttributes
  ): AchAcknowledgeRequest {
    const isPtrs = accountInfo.classifications.isPtrs;
    const isIncoming = transactionForm.extraPrompts.transferDirection === 'incoming';
    const cashieringReqId = authorizationId.includes('-') ? +authorizationId.split('-')[1] : parseInt(authorizationId);
    const ackRequest: AchAcknowledgeRequest = {
      subAccountId: accountInfo.accountId,
      masterAccountId: accountInfo.masterAccount,
      cashieringRequestId: cashieringReqId,
      amount: currencyStringToNumber(transactionForm.transactionDetails.amount).toString(),
      //TODO: probably get processDate from input response instead
      processDate: transactionForm.transactionDetails.completionDate.replace(/\//g, '-') ?? '',
      endDate: this.datepipe.transform(transactionForm.transactionDetails.endDate, 'MM/dd/yyyy') ?? '',
      secondProcessDate: this.datepipe.transform(transactionForm.transactionDetails.secondProcessDate, 'MM/dd/yyyy') ?? '',
      frequency: Boolean(achInfo?.frequency) ? (achInfo?.frequency ?? '') : achInfo.frequencyList.find((x) => x.code === transactionForm.transactionDetails.recurringFrequency)?.description ?? '',
      contributionYear: transactionForm.transactionDetails.contributionReason ? transactionForm.transactionDetails.contributionReason?.substring(0, 4) : '',
      fundsDirection: transactionForm.extraPrompts.transferDirection === 'incoming' ? 'I' : 'O',
      accountPlatformCode: '',
      isEdit: false,
      grossNetIndicator: transactionForm.transactionDetails.amountType === amountTypeDict.gross, //true for Gross, false for Net
      irsDistributionCode: !!initReleaseTransactionInContext?.draftId
        ? initReleaseTransactionInContext.draftObject.irsDistributionCode ?? ' '
        : achInfo.distributionReasonsList?.find((element) => element.description === transactionForm.transactionDetails.taxWithholdingsFormGroup?.distributionReason)?.code.toString() ?? ' ',
      irsDistributionSubCode: achInfo.distributionReasonsList?.find((element) => element.description === transactionForm.transactionDetails.taxWithholdingsFormGroup?.distributionReason)?.subCode.toString() ?? ' ',
      stateTaxRate: transactionForm.transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent?.replace('%', ''),
      fedTaxRate: transactionForm.transactionDetails.taxWithholdingsFormGroup?.fedTaxPercent?.replace('%', ''),
      acknowledgementConsentDateTime: acknowledgementConsentDateTime,
      trustFieldsRequest: {
        trustIncmPct: isPtrs ? (transactionForm.transactionDetails.trustIncomePercent?.replace('%', '') ?? '') : '',
        trustPrinPct: isPtrs ? (transactionForm.transactionDetails.trustPrincipalPercent?.replace('%', '') ?? '') : '',
        trustTaxCode: isPtrs ? (transactionForm.transactionDetails.trustTaxCode ?? '') : '',
        tranMemo: isPtrs ? (transactionForm.transactionDetails.trustComment ?? '') : '',
        taxCategory: isPtrs ? (transactionForm.transactionDetails.trustTaxCategory ?? '') : '',
      },
      addProfile: true,
      addProfileAttributes: {
        fundsDirection: transactionForm.extraPrompts.transferDirection === 'incoming' ? 'DEP' : 'DIS',
        bankAccountTypeCode: achInfo.fiAccountType,
        bankAccountId: achInfo.toFromAccount.accountNumber,
        aba: achInfo.toFromAccount.abaNumber,
        maxTransferAmount: Number(achInfo.maxTransferAmount?.replace('$', '').split(',')
          .join('')),
        bankAccountTitleName: achInfo.toFromAccount.accountName,
        contributionCode: achInfo.contributionCode,
        contributionSubCode: achInfo.contributionSubCode,
        financialInstitution: achInfo.toFromAccount.financialInstitution
      },
      draftId: achInfo.authResponse?.draftId ?? ''
    };

    if (isPtrs) {
      ackRequest.trustFieldsRequestInfo = {
        incomePercent: Number(transactionForm.transactionDetails.trustIncomePercent?.replace('%', '')),
        principalPercent: Number(transactionForm.transactionDetails.trustPrincipalPercent?.replace('%', '')),
        paidForContact: paidForInfo.individuals.find((individual) => transactionForm.transactionDetails.trustPaidFor?.toLocaleLowerCase().includes(individual.name.toLocaleLowerCase()) && transactionForm.transactionDetails.trustPaidFor?.toLocaleLowerCase().includes(individual.role.toLocaleLowerCase())) ?? new PaidForDetails(),
        settlementDate: isIncoming ? transactionForm.transactionDetails.settlementDate : transactionForm.transactionDetails.completionDate,
        tradeDate: transactionForm.transactionDetails.withdrawalDate,
        taxCode: transactionForm.transactionDetails.trustTaxCode ?? '',
        comment: transactionForm.transactionDetails.trustComment ?? '',
        accountNumber: toFromAccount.accountNumber,
        accountName: toFromAccount.accountName,
        accountType: fiAccountTypeCode,
        financialInstitution: toFromAccount.financialInstitution,
        aba: toFromAccount.abaNumber
      };
    }
    return ackRequest;
  }

  createAchAcknowledgeRequest(
    accountInfo: AccountInfoState,
    authorizationId: string,
    transactionForm: AchForm,
    acknowledgementConsentDateTime: string,
    paidForInfo: PaidFor,
    toFromAccount: ToFromAccount,
    fiAccountTypeCode: string,
    achInfo: AchInfo,
    initReleaseTransactionInContext: DraftListAttributes
  ): AchAcknowledgeRequest {
    const isPtrs = accountInfo.classifications.isPtrs;
    const isIncoming = transactionForm.extraPrompts.transferDirection === 'incoming';
    const ackRequest: AchAcknowledgeRequest = {
      subAccountId: accountInfo.accountId,
      masterAccountId: accountInfo.masterAccount,
      cashieringRequestId: +authorizationId,
      amount: currencyStringToNumber(transactionForm.transactionDetails.amount).toString(),
      //TODO: probably get processDate from input response instead
      processDate: (isIncoming
        ? transactionForm.transactionDetails.settlementDate.replace(/\//g, '-')
        : transactionForm.transactionDetails.completionDate.replace(/\//g, '-')) ?? '',
      endDate: '',
      secondProcessDate: '',
      contributionYear: transactionForm.transactionDetails.contributionReason ? transactionForm.transactionDetails.contributionReason?.substring(0, 4) : '',
      fundsDirection: transactionForm.extraPrompts.transferDirection === 'incoming' ? 'I' : 'O',
      accountPlatformCode: '',
      frequency: '',
      isEdit: false,
      grossNetIndicator: transactionForm.transactionDetails.amountType === amountTypeDict.gross,
      irsDistributionCode: initReleaseTransactionInContext
        ? initReleaseTransactionInContext.draftObject.irsDistributionCode ?? ' '
        : achInfo.distributionReasonsList?.find((element) => element.description === transactionForm.transactionDetails.taxWithholdingsFormGroup?.distributionReason)?.code.toString() ?? ' ',
      irsDistributionSubCode: '',
      stateTaxRate: transactionForm.transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent?.replace('%', ''),
      fedTaxRate: transactionForm.transactionDetails.taxWithholdingsFormGroup?.fedTaxPercent?.replace('%', ''),
      acknowledgementConsentDateTime: acknowledgementConsentDateTime,
      addProfile: false,
      trustFieldsRequest: {
        trustIncmPct: isPtrs ? (transactionForm.transactionDetails.trustIncomePercent?.replace('%', '') ?? '') : '',
        trustPrinPct: isPtrs ? (transactionForm.transactionDetails.trustPrincipalPercent?.replace('%', '') ?? '') : '',
        trustTaxCode: isPtrs ? (transactionForm.transactionDetails.trustTaxCode ?? '') : '',
        tranMemo: isPtrs ? (transactionForm.transactionDetails.trustComment ?? '') : '',
        taxCategory: isPtrs ? (transactionForm.transactionDetails.trustTaxCategory ?? '') : '',
      },
      draftId: achInfo.authResponse?.draftId ?? ''
    };

    if (isPtrs) {
      ackRequest.trustFieldsRequestInfo = {
        incomePercent: Number(transactionForm.transactionDetails.trustIncomePercent?.replace('%', '')),
        principalPercent: Number(transactionForm.transactionDetails.trustPrincipalPercent?.replace('%', '')),
        paidForContact: initReleaseTransactionInContext?.draftObject?.paidFor ? initReleaseTransactionInContext.draftObject.paidFor : this.getPaidForDetails(paidForInfo, transactionForm.transactionDetails),
        settlementDate: isIncoming ? transactionForm.transactionDetails.settlementDate : transactionForm.transactionDetails.completionDate,
        tradeDate: transactionForm.transactionDetails.withdrawalDate,
        taxCode: transactionForm.transactionDetails.trustTaxCode ?? '',
        comment: transactionForm.transactionDetails.trustComment ?? '',
        accountNumber: toFromAccount.accountNumber,
        accountName: toFromAccount.accountName,
        accountType: fiAccountTypeCode,
        financialInstitution: toFromAccount.financialInstitution,
        aba: toFromAccount.abaNumber
      };
    }
    return ackRequest;
  }

  createAchAcknowledgeRequestStandingAuth(accountInfo: AccountInfoState, authorizationId: string, standingAuthForm: AchStandingAuthForm, acknowledgementConsentDateTime: string, achInfo: AchInfo): AchAcknowledgeRequest {
    const ackRequest: AchAcknowledgeRequest = {
      subAccountId: accountInfo.accountId,
      masterAccountId: accountInfo.masterAccount,
      cashieringRequestId: +authorizationId.split('-')[1],
      amount: currencyStringToNumber(standingAuthForm.standingAuthorizationDetails.amount).toString(),
      processDate: this.GetProcessDateAcknowledge(standingAuthForm),
      endDate: this.datepipe.transform(standingAuthForm.standingAuthorizationDetails.endDate, 'MM/dd/yyyy') ?? '12/31/9999',
      secondProcessDate: this.datepipe.transform(standingAuthForm.standingAuthorizationDetails.secondProcessDate, 'MM/dd/yyyy') ?? '12/31/9999',
      contributionYear: standingAuthForm.standingAuthorizationDetails.contributionReason ? standingAuthForm.standingAuthorizationDetails.contributionReason?.substring(0, 4) : '',
      fundsDirection: standingAuthForm.extraPrompts.transferDirection === 'incoming' ? 'I' : 'O',
      accountPlatformCode: '',
      frequency: achInfo?.frequencyList ? achInfo.frequencyList.find((frequency) => frequency.code === standingAuthForm.standingAuthorizationDetails.recurringFrequency)?.description ?? standingAuthForm.standingAuthorizationDetails.recurringFrequency : standingAuthForm.standingAuthorizationDetails.recurringFrequency,
      isEdit: true,
      irsDistributionCode: achInfo.distributionReasonMap?.find((element) => element.description === standingAuthForm.standingAuthorizationDetails.taxWithholdingsFormGroup?.distributionReason)?.code.toString() ?? ' ',
      irsDistributionSubCode: achInfo.distributionReasonMap?.find((element) => element.description === standingAuthForm.standingAuthorizationDetails.taxWithholdingsFormGroup?.distributionReason)?.subCode.toString() ?? ' ',
      grossNetIndicator: standingAuthForm.standingAuthorizationDetails.amountType === amountTypeDict.gross,
      stateTaxRate: standingAuthForm.standingAuthorizationDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', '') === ''
        ? '0'
        : standingAuthForm.standingAuthorizationDetails.taxWithholdingsFormGroup?.stateTaxPercent.replace('%', ''),
      fedTaxRate: standingAuthForm.standingAuthorizationDetails.taxWithholdingsFormGroup?.fedTaxPercent.replace('%', '') === ''
        ? '0'
        : standingAuthForm.standingAuthorizationDetails.taxWithholdingsFormGroup?.fedTaxPercent.replace('%', ''),
      acknowledgementConsentDateTime: acknowledgementConsentDateTime,
      addProfile: false,
      trustFieldsRequest: {
        trustIncmPct: '',
        trustPrinPct: '',
        trustTaxCode: '',
        tranMemo: '',
        taxCategory: '',
      },
      draftId: ''
    };
    return ackRequest;
  }

  GetProcessDateVerify(standingAuthDetails: AchStandingAuthDetails, achStandingAuthForm: AchStandingAuthForm) {
    if (standingAuthDetails.recurringFrequency === 'On-Request') {
      return '12/31/9999';
    }
    if (achStandingAuthForm.extraPrompts.transferDirection === 'incoming') {
      return this.datepipe.transform(standingAuthDetails.settlementDate, 'MM/dd/yyyy') ?? '12/31/9999';
    }
    return this.datepipe.transform(standingAuthDetails.completionDate, 'MM/dd/yyyy') ?? '12/31/9999';
  }

  GetProcessDateAcknowledge(achStandingAuthForm: AchStandingAuthForm) {
    if (achStandingAuthForm.standingAuthorizationDetails.recurringFrequency === 'On-Request') {
      return '12/31/9999';
    }
    if (achStandingAuthForm.extraPrompts.transferDirection === 'incoming') {
      return this.datepipe.transform(achStandingAuthForm.standingAuthorizationDetails.settlementDate, 'MM/dd/yyyy') ?? '12/31/9999';
    }
    return this.datepipe.transform(achStandingAuthForm.standingAuthorizationDetails.completionDate, 'MM/dd/yyyy') ?? '12/31/9999';
  }

  getPaidForDetails(paidForInfo: PaidFor, transactionDetails: AchTransactionDetails) {
    const paidFor: PaidForDetails = paidForInfo.individuals.find((individual) => transactionDetails.trustPaidFor?.toLocaleLowerCase().includes(individual.name.toLocaleLowerCase())
      && transactionDetails.trustPaidFor?.toLocaleLowerCase().includes(individual.role.toLocaleLowerCase())) ?? new PaidForDetails();

    return paidFor;
  }

  // Only forward a stored transferId when it belongs to the current cashiering context.
  // Prevents stale IDs leaking across transaction-type changes or new transactions.
  getTransferIdForContext(achInfo: AchInfo, cashieringRequestId: number): string {
    return achInfo.cashieringRequestId !== '' && +achInfo.cashieringRequestId === cashieringRequestId
      ? (achInfo.transferId ?? '')
      : '';
  }
}
